import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import SnowFallComponent from 'components/SnowFallComponent';
import ClienteForm from './ClienteForm';
import { clientesService } from '../../services/clientesService';
import { empresasService } from '../../services/empresasService';
import { sucursalesService } from '../../services/sucursalesService';

function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formKey, setFormKey] = useState(0); // Key para forzar remontaje del formulario
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filtroSucursal, setFiltroSucursal] = useState('');
  const [buscarTexto, setBuscarTexto] = useState('');
  const loadingClientesRef = useRef(false);

  useEffect(() => {
    loadEmpresaYSucursales();
  }, []);

  const loadClientes = async () => {
    // Evitar cargas múltiples simultáneas
    if (loadingClientesRef.current) return;
    
    try {
      loadingClientesRef.current = true;
      setLoading(true);
      setError('');
      const empresaId = empresa ? (empresa.EmpresaID || empresa.empresaID) : null;
      const sucursalId = filtroSucursal ? parseInt(filtroSucursal) : null;
      const buscar = buscarTexto.trim() || null;
      const response = await clientesService.getAll(empresaId, sucursalId, buscar);
      if (response.success && response.data) {
        const clientesData = Array.isArray(response.data) ? response.data : [];
        // Normalizar los datos para manejar tanto camelCase como PascalCase
        const normalizedData = clientesData.map(cli => ({
          ClienteID: cli.ClienteID || cli.clienteID,
          EmpresaID: cli.EmpresaID || cli.empresaID,
          SucursalID: cli.SucursalID || cli.sucursalID,
          NombreRazonSocial: cli.NombreRazonSocial || cli.nombreRazonSocial || '',
          RFC: cli.RFC || cli.rfc,
          UsoCFDI: cli.UsoCFDI || cli.usoCFDI,
          CorreoElectronico: cli.CorreoElectronico || cli.correoElectronico,
          DomicilioFiscal: cli.DomicilioFiscal || cli.domicilioFiscal,
          CodigoPostal: cli.CodigoPostal || cli.codigoPostal,
          RegimenFiscal: cli.RegimenFiscal || cli.regimenFiscal,
          Telefono: cli.Telefono || cli.telefono,
          Activo: cli.Activo !== undefined ? cli.Activo : (cli.activo !== undefined ? cli.activo : true),
        }));
        setClientes(normalizedData);
      } else {
        setError(response.Message || response.message || 'Error al cargar clientes');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
      loadingClientesRef.current = false;
    }
  };

  useEffect(() => {
    if (!loadingSucursales && empresa) {
      loadClientes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSucursales, empresa, filtroSucursal, buscarTexto]);

  // Verificar si hay un ID en sessionStorage para abrir el detalle/edición automáticamente
  useEffect(() => {
    const openDetailId = sessionStorage.getItem('openDetail_cliente');
    if (openDetailId && !loading && empresa) {
      // Cargar el cliente específico desde la API para asegurar que esté disponible
      // independientemente del filtro de sucursal
      const loadClienteFromAPI = async () => {
        try {
          const response = await clientesService.getById(openDetailId);
          if (response.success && response.data) {
            sessionStorage.removeItem('openDetail_cliente');
            handleEdit(response.data);
          } else {
            // Si no se encuentra en la API, intentar buscarlo en la lista actual
            sessionStorage.removeItem('openDetail_cliente');
            const cliente = clientes.find(c => 
              (c.ClienteID || c.clienteID)?.toString() === openDetailId
            );
            if (cliente) {
              handleEdit(cliente);
            }
          }
        } catch (error) {
          // Si hay error, intentar buscarlo en la lista actual como fallback
          sessionStorage.removeItem('openDetail_cliente');
          const cliente = clientes.find(c => 
            (c.ClienteID || c.clienteID)?.toString() === openDetailId
          );
          if (cliente) {
            handleEdit(cliente);
          }
        }
      };
      
      loadClienteFromAPI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, clientes, empresa]);

  const loadEmpresaYSucursales = async () => {
    try {
      setLoadingSucursales(true);
      // Obtener la empresa activa (solo hay una)
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          // Solo actualizar si el ID cambió para evitar re-renders innecesarios
          const empresaId = empresaActiva.EmpresaID || empresaActiva.empresaID;
          if (!empresa || (empresa.EmpresaID || empresa.empresaID) !== empresaId) {
            setEmpresa(empresaActiva);
          }
          // Cargar sucursales de la empresa
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            const normalized = sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            }));
            setSucursales(normalized);
            // Seleccionar la primera sucursal por defecto si hay sucursales y no hay una seleccionada
            if (normalized.length > 0 && !filtroSucursal) {
              setFiltroSucursal(normalized[0].SucursalID.toString());
            }
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    } finally {
      setLoadingSucursales(false);
    }
  };


  const handleCreate = () => {
    setEditingCliente(null);
    setFormKey(prev => prev + 1); // Incrementar key para forzar remontaje del formulario
    setShowForm(true);
  };

  const handleEdit = async (cliente) => {
    try {
      const clienteId = cliente.ClienteID || cliente.clienteID;
      const response = await clientesService.getById(clienteId);
      if (response.success && response.data) {
        setEditingCliente(response.data);
        setShowForm(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar los datos del cliente');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    }
  };

  const handleDelete = (cliente) => {
    setClienteToDelete(cliente);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!clienteToDelete) return;

    try {
      setDeleting(true);
      const clienteId = clienteToDelete.ClienteID || clienteToDelete.clienteID;
      const response = await clientesService.delete(clienteId);
      if (response.success) {
        setSuccess('Cliente desactivado exitosamente');
        setShowDeleteModal(false);
        setClienteToDelete(null);
        loadClientes();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.Message || response.message || 'Error al desactivar cliente');
      }
    } catch (err) {
      setError(err.message || 'Error de conexión al desactivar cliente');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingCliente(null);
    setSuccess(editingCliente ? 'Cliente actualizado exitosamente' : 'Cliente creado exitosamente');
    loadClientes();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCliente(null);
    setFormKey(prev => prev + 1); // Incrementar key para resetear el formulario la próxima vez
  };

  const getSucursalNombre = (sucursalId) => {
    if (!sucursalId) return 'Todas las sucursales';
    const sucursal = sucursales.find(s => (s.SucursalID || s.sucursalID) === sucursalId);
    return sucursal ? (sucursal.Nombre || sucursal.nombre) : '-';
  };

  const columns = [
    {
      key: 'sucursal',
      label: 'Sucursal',
      accessor: 'SucursalID',
      render: (value) => getSucursalNombre(value),
    },
    {
      key: 'nombreRazonSocial',
      label: 'Nombre / Razón Social',
      accessor: 'NombreRazonSocial',
    },
    {
      key: 'rfc',
      label: 'RFC',
      accessor: 'RFC',
      render: (value) => value || '-',
    },
    {
      key: 'correoElectronico',
      label: 'Correo',
      accessor: 'CorreoElectronico',
      render: (value) => value || '-',
    },
    {
      key: 'telefono',
      label: 'Teléfono',
      accessor: 'Telefono',
      render: (value) => value || '-',
    },
    {
      key: 'activo',
      label: 'Estado',
      accessor: 'Activo',
      render: (value) => (
        <Badge color={value ? 'success' : 'secondary'}>
          {value ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => (
        <div>
          <Button
            color="info"
            size="sm"
            className="mr-2"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
          >
            <i className="tim-icons icon-pencil" />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
          >
            <i className="tim-icons icon-simple-remove" />
          </Button>
        </div>
      ),
    },
  ];

  if (showForm) {
    return (
      <div className="content">
        <SnowFallComponent />
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">
                  {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ClienteForm
                  key={formKey} // Key para forzar remontaje cuando cambia
                  cliente={editingCliente}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Clientes</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button color="primary" onClick={handleCreate}>
                    <i className="tim-icons icon-simple-add" /> Nuevo Cliente
                  </Button>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <select
                    className="form-control"
                    value={filtroSucursal}
                    onChange={(e) => setFiltroSucursal(e.target.value)}
                    disabled={loadingSucursales}
                  >
                    <option value="">Todas las sucursales</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md="8">
                  <InputGroup>
                    <InputGroupText>
                      <i className="tim-icons icon-zoom-split" />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Buscar por nombre, RFC o correo..."
                      value={buscarTexto}
                      onChange={(e) => setBuscarTexto(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={clientes} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setClienteToDelete(null);
        }}
        title="Desactivar Cliente"
        message={`¿Está seguro de desactivar el cliente "${clienteToDelete?.NombreRazonSocial || clienteToDelete?.nombreRazonSocial}"?`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}

export default Clientes;

